import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { GitHubIcon, GoogleIcon, DiscordIcon } from '../components/OAuthIcons'
import { oauthConfig } from '../lib/oauth-config'
import { ProfileService } from '../services/profileService'
import { UserProfileManager } from '../utils/userProfileManager'
import { Check, X, User, ArrowRight } from 'lucide-react'
import LogoFiverFlow from '../assets/LogoFiverFlow.png'
import IconVert from '../assets/Icon - Vert.png'

const AuthRegister: React.FC = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState<'register' | 'username'>('register')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  // État pour le formulaire de username
  const [username, setUsername] = useState('')
  const [usernameError, setUsernameError] = useState('')
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle')
  const [isCheckingUsername, setIsCheckingUsername] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({ email, password })
      
      if (signUpError) {
        setError(signUpError.message)
        return
      }

      if (data?.user) {
        setUserId(data.user.id)
        
        // S'assurer que le profil utilisateur existe
        await UserProfileManager.ensureProfile(data.user)
        
        // Vérifier si l'utilisateur a déjà un username
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('username')
          .eq('user_id', data.user.id)
          .maybeSingle()
        
        if (profile?.username) {
          // Si l'utilisateur a déjà un username, rediriger vers le dashboard
          navigate('/dashboard')
        } else {
          // Sinon, passer à l'étape de création de username
          setStep('username')
        }
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSocialLogin = async (provider: 'github' | 'google' | 'discord') => {
    setLoading(true)
    setError('')
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          ...oauthConfig.options,
          // S'assurer que la redirection pointe vers notre domaine Fiverflow
          redirectTo: oauthConfig.redirectTo,
          // Personnaliser le nom de l'application (si supporté par le provider)
          queryParams: {
            ...oauthConfig.queryParams,
            // Pour GitHub: utiliser le nom de l'application configuré dans Supabase
            // Pour Google: le nom vient de la configuration OAuth dans Google Cloud Console
            // Pour Discord: le nom vient de la configuration de l'application Discord
            // Note: Le nom "Fiverflow" doit être configuré dans le dashboard Supabase
            // Authentication > Settings > Application Name
          }
        }
      })
      if (error) setError(error.message)
      // Note: signInWithOAuth redirige automatiquement, donc on ne set pas loading à false ici
      // car le composant sera démonté lors de la redirection
    } catch (err: any) {
      setError(err.message)
      setLoading(false)
    }
  }

  // Validation et vérification du username
  useEffect(() => {
    if (step !== 'username' || !username || username.length < 3) {
      setUsernameStatus('idle')
      setUsernameError('')
      return
    }

    const timeoutId = setTimeout(async () => {
      // Validation du format
      if (!ProfileService.validateUsername(username.toLowerCase())) {
        setUsernameStatus('invalid')
        setUsernameError('Nom d\'utilisateur invalide. Utilisez uniquement des lettres minuscules, chiffres et underscores (3-50 caractères). Ne peut pas commencer par un chiffre ni se terminer par un underscore.')
        setIsCheckingUsername(false)
        return
      }

      setIsCheckingUsername(true)
      setUsernameStatus('checking')

      try {
        const isAvailable = await ProfileService.checkUsernameAvailability(username.toLowerCase(), userId || undefined)
        if (isAvailable) {
          setUsernameStatus('available')
          setUsernameError('')
        } else {
          setUsernameStatus('taken')
          setUsernameError('Ce nom d\'utilisateur est déjà pris')
        }
      } catch (err) {
        setUsernameStatus('taken')
        setUsernameError('Erreur lors de la vérification')
      } finally {
        setIsCheckingUsername(false)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [username, step, userId])

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (usernameStatus !== 'available' || !userId) {
      return
    }

    setLoading(true)
    setUsernameError('')

    try {
      const result = await ProfileService.updateUsername(userId, username.toLowerCase())
      
      if (result.success) {
        navigate('/dashboard')
      } else {
        setUsernameError(result.error || 'Erreur lors de la création du nom d\'utilisateur')
      }
    } catch (err: any) {
      setUsernameError('Une erreur inattendue s\'est produite.')
    } finally {
      setLoading(false)
    }
  }

  const handleUsernameChange = (value: string) => {
    // Convertir en minuscules et remplacer les espaces et caractères spéciaux
    const normalized = value.toLowerCase().replace(/[^a-z0-9_]/g, '')
    setUsername(normalized)
    setUsernameError('')
  }

  // Bloquer le retour en arrière pendant le formulaire username
  useEffect(() => {
    if (step === 'username') {
      // Empêcher le retour en arrière
      const handlePopState = (event: PopStateEvent) => {
        event.preventDefault()
        window.history.pushState(null, '', window.location.href)
      }

      // Push un état pour empêcher le retour
      window.history.pushState(null, '', window.location.href)
      window.addEventListener('popstate', handlePopState)

      return () => {
        window.removeEventListener('popstate', handlePopState)
      }
    }
  }, [step])

  // Rendu du formulaire de username
  if (step === 'username') {
    return (
      <div className="min-h-screen h-screen w-full bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 overflow-hidden">
        <div className="flex h-full flex-col lg:flex-row">
          {/* Left: username form */}
          <div className="flex flex-1 items-center justify-center px-4 py-6 sm:px-8">
            <div className="w-full max-w-md space-y-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="flex items-center gap-3">
                  <img src={IconVert} alt="FiverFlow Icon" className="h-8 w-auto" />
                  <div className="hidden sm:block h-6 w-px bg-gray-200 dark:bg-slate-700" />
                  <img src={LogoFiverFlow} alt="FiverFlow" className="h-6 w-auto" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold">Choose your username</h1>
                  <p className="text-sm text-gray-600 dark:text-slate-400">This will be your unique identifier on FiverFlow</p>
                </div>
              </div>

              <form onSubmit={handleUsernameSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="username" className="text-sm font-medium">Username</label>
                  <div className="relative">
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => handleUsernameChange(e.target.value)}
                      className={`w-full px-3 py-3 text-sm border rounded-xl bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                        usernameStatus === 'available'
                          ? 'border-green-500 focus:ring-green-500'
                          : usernameStatus === 'taken' || usernameStatus === 'invalid'
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-200 dark:border-slate-700 focus:ring-[#9c68f2]'
                      }`}
                      placeholder="your_username"
                      required
                      minLength={3}
                      maxLength={50}
                      autoFocus
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isCheckingUsername ? (
                        <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                      ) : usernameStatus === 'available' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : username.length > 0 && usernameStatus !== 'idle' ? (
                        <X className="w-4 h-4 text-red-500" />
                      ) : null}
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-slate-400 space-y-0.5 px-1">
                    <p>• 3-50 characters</p>
                    <p>• Lowercase letters, numbers and underscores only</p>
                    <p>• Cannot start with a number</p>
                    <p>• Cannot end with an underscore</p>
                    <p>• Must be unique</p>
                  </div>
                </div>

                {usernameError && <div className="text-rose-400 text-sm px-1">{usernameError}</div>}
                <button
                  type="submit"
                  disabled={usernameStatus !== 'available' || loading}
                  className="w-full py-3 bg-[#9c68f2] hover:bg-[#8655e6] disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>Continue</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              <div className="text-center text-sm text-gray-600 dark:text-slate-400">
                <button
                  onClick={async () => {
                    await supabase.auth.signOut()
                    navigate('/login')
                  }}
                  className="text-[#9c68f2] hover:text-[#8655e6] font-medium"
                >
                  Back to login
                </button>
              </div>
            </div>
          </div>

          <div className="hidden lg:flex flex-1 items-center justify-center bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700">
            <div className="max-w-md px-10 space-y-4 text-center">
              <h2 className="text-2xl font-semibold">Complete your profile</h2>
              <p className="text-gray-600 dark:text-slate-400">Your username helps others find and connect with you on the platform.</p>
              <div className="grid gap-3">
                {['Unique identifier','Public profile','Easy sharing'].map((t) => (
                  <div key={t} className="rounded-xl border border-gray-200 dark:border-slate-700 p-4 text-sm text-gray-600 dark:text-slate-400">
                    {t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Rendu du formulaire d'inscription
  return (
    <div className="min-h-screen h-screen w-full bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 overflow-hidden">
      <div className="flex h-full flex-col lg:flex-row">
        {/* Left: registration form */}
        <div className="flex flex-1 items-center justify-center px-4 py-6 sm:px-8">
          <div className="w-full max-w-md space-y-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex items-center gap-3">
                <img src={IconVert} alt="FiverFlow Icon" className="h-8 w-auto" />
                <div className="hidden sm:block h-6 w-px bg-gray-200 dark:bg-slate-700" />
                <img src={LogoFiverFlow} alt="FiverFlow" className="h-6 w-auto" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">Create your account</h1>
                <p className="text-sm text-gray-600 dark:text-slate-400">Start your journey with FiverFlow</p>
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => handleSocialLogin('github')}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 text-sm"
              >
                <GitHubIcon className="w-4 h-4 mr-2 text-gray-900 dark:text-slate-100" />
                <span className="truncate">Continue with GitHub</span>
              </button>
              <button
                onClick={() => handleSocialLogin('google')}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 text-sm"
              >
                <GoogleIcon className="w-4 h-4 mr-2" />
                <span className="truncate">Continue with Google</span>
              </button>
              <button
                onClick={() => handleSocialLogin('discord')}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 text-sm"
              >
                <DiscordIcon className="w-4 h-4 mr-2 text-indigo-400" />
                <span className="truncate">Continue with Discord</span>
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-wide text-gray-600 dark:text-slate-400">
                <span className="px-3 bg-white dark:bg-slate-900">or email</span>
              </div>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-3 text-sm border border-gray-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2]"
                  placeholder="Enter your email"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-3 text-sm border border-gray-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2]"
                  placeholder="Create a password"
                />
              </div>
              {error && <div className="text-rose-400 text-sm px-1">{error}</div>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#9c68f2] hover:bg-[#8655e6] text-white font-medium rounded-xl transition-colors disabled:opacity-50 text-sm"
              >
                {loading ? 'Creating...' : 'Create account'}
              </button>
            </form>

            <div className="text-center text-sm text-gray-600 dark:text-slate-400">
              Already have an account?{' '}
              <button onClick={() => navigate('/login')} className="text-[#9c68f2] hover:text-[#8655e6] font-medium">Sign In</button>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex flex-1 items-center justify-center bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700">
          <div className="max-w-md px-10 space-y-4 text-center">
            <h2 className="text-2xl font-semibold">Everything you need</h2>
            <p className="text-gray-600 dark:text-slate-400">Join a modern platform designed for speed, reliability and growth.</p>
            <div className="grid gap-3">
              {['Authentication','Team tools','Scalable infra'].map((t) => (
                <div key={t} className="rounded-xl border border-gray-200 dark:border-slate-700 p-4 text-sm text-gray-600 dark:text-slate-400">
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthRegister


